// @flow
import { useState, useRef, useEffect } from 'react';
import merge from 'lodash/merge';
import { useFormSubmitterContext } from '../form-submitter/form-submitter-context';
import { OperatorType, Operators } from './constants';
import { logicEngine } from './common/logic-engine';
import type { LogicItem } from './logic-builder-types';

export const OperatorToExpression: {
  [OperatorType]: (fieldId: string, value: any) => string,
} = {
  [Operators.EQUALS]: (fieldId, value) =>
    typeof value === 'boolean'
      ? `${fieldId} = ${value}`
      : `${fieldId} = '${value}'`,
  [Operators.DOES_NOT_EQUAL]: (fieldId, value) =>
    typeof value === 'boolean'
      ? `${fieldId} != ${value}`
      : `${fieldId} != '${value}'`,
  [Operators.GREATER_THAN]: (fieldId, value) => `${fieldId} > ${value}`,
  [Operators.GREATER_THAN_OR_EQUAL]: (fieldId, value) =>
    `${fieldId} >= ${value}`,
  [Operators.LESS_THAN]: (fieldId, value) => `${fieldId} < ${value}`,
  [Operators.LESS_THAN_OR_EQUAL]: (fieldId, value) => `${fieldId} <= ${value}`,
  [Operators.CONTAINS]: (fieldId, value) => `$contains(${fieldId}, /${value}/)`,
  [Operators.DOES_NOT_CONTAIN]: (fieldId, value) =>
    `$not($contains(${fieldId}, /${value}/))`,
  [Operators.BEGINS_WITH]: (fieldId, value) =>
    `$contains(${fieldId}, /^${value}/)`,
  [Operators.ENDS_WITH]: (fieldId, value) =>
    `$contains(${fieldId}, /${value}$/)`,
  [Operators.IS_EMPTY]: fieldId => `${fieldId} = ""`,
  [Operators.IS_NOT_EMPTY]: fieldId => `${fieldId} != ""`,
};

const generateJsonataExpression = (
  fieldId: string,
  operator: OperatorType,
  value: any,
) => {
  const escapedFieldId = `$.\`${fieldId}\``;

  if (operator in OperatorToExpression) {
    return OperatorToExpression[operator](escapedFieldId, value);
  }

  throw new Error(`Unknown operator: ${operator}`);
};

const computeLogicItems = (
  logicContent: Array<LogicItem>,
): Array<LogicItem> => {
  return logicContent.map((logicItem: LogicItem): LogicItem => ({
    ...logicItem,
    conditionGroups: logicItem.conditionGroups.map(conditionGroup => ({
      ...conditionGroup,
      conditions: conditionGroup.conditions.map(condition => ({
        ...condition,
        expression: condition.fieldId
          ? generateJsonataExpression(
              condition.fieldId,
              condition.operator,
              condition.value,
            )
          : undefined,
      })),
    })),
  }));
};

const applyLogicEngine = async (
  preComputedLogics,
  response,
  dispatch,
  initialComponents,
) => {
  const stateChanges = await logicEngine(preComputedLogics, response);
  const mergedStateChanges = merge({}, initialComponents, stateChanges);

  dispatch({
    type: 'ACTION_UPDATE_COMPONENTS',
    payload: mergedStateChanges,
  });
};

const useFormLogic = () => {
  const { state, dispatch } = useFormSubmitterContext();
  const initialComponentsRef = useRef(null);
  const isMounted = useRef(true);

  // Track component mounted state
  useEffect(() => {
    isMounted.current = true;

    return function cleanup() {
      isMounted.current = false;
    };
  }, []);

  // Initialize the initialComponentsRef once the state.components is populated
  useEffect(() => {
    if (
      !initialComponentsRef.current &&
      state.components &&
      Object.keys(state.components).length > 0
    ) {
      initialComponentsRef.current = state.components;
    }
  }, [state.components]);

  const [loadedLogicContent, setLoadedLogicContent] = useState(null);
  const preComputedLogicsRef = useRef();

  // Process and cache the logic content once it's loaded
  useEffect(() => {
    if (!loadedLogicContent && state.form?.content?.logic?.length > 0) {
      preComputedLogicsRef.current = computeLogicItems(
        state.form.content.logic,
      );
      setLoadedLogicContent(state.form.content.logic);
    }
  }, [loadedLogicContent, state.form]);

  useEffect(() => {
    if (preComputedLogicsRef.current && isMounted.current) {
      applyLogicEngine(
        preComputedLogicsRef.current,
        state.response,
        dispatch,
        initialComponentsRef.current,
      );
    }
  }, [dispatch, state.response]);
};

export { computeLogicItems, generateJsonataExpression, useFormLogic };
